import { Button } from '@/components/ui-kit/button';
import { ArrowLeft, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetMyEmployee } from '../../hooks/use-employees';
import { useProfileCompletion } from '../../hooks/use-profile-completion';
import { useGetSkillsFor, useGetSkillTaxonomy } from '../../hooks/use-skill-lookups';
import { skillLookupOf } from '../../schemas/skills-schema';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';
import { EmployeeEditSkeleton } from '../directory-skeletons/directory-skeletons';
import { EmployeeEditTabs } from '../employee-form/employee-edit-tabs';
import { APP_PATHS } from '@/constant/app-paths';
import { MY_PROFILE_TABS } from '../../constants/profile-tabs';

/**
 * "Edit profile" for the signed-in person: their own basic info, contact details and skills, one tab each.
 * The skill lookups are loaded first because stored skills (which may come from the legacy app) are
 * matched to today's skills and sub-categories when the form is filled.
 */
export const SelfProfileEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetMyEmployee();
  const { data: taxonomy, isLoading: isTaxonomyLoading } = useGetSkillTaxonomy();
  const { isCompletionRequired } = useProfileCompletion();

  const employee: Employee | undefined = (data as { Employees?: EmployeesPagingResult })?.Employees
    ?.items?.[0];
  const lookup = employee ? skillLookupOf(employee, taxonomy?.subCategories ?? []) : undefined;
  const canLoadSkills = Boolean(lookup && taxonomy);
  const { data: skills = [], isPending: isSkillsPending } = useGetSkillsFor(
    lookup?.skillIds ?? [],
    lookup?.subCategoryIds ?? [],
    canLoadSkills
  );

  if (isLoading || isTaxonomyLoading || (canLoadSkills && isSkillsPending)) {
    return (
      <div className="flex w-full flex-col gap-4">
        <h1 className="text-lg font-semibold">{t('EDIT_PROFILE')}</h1>
        <EmployeeEditSkeleton />
      </div>
    );
  }

  if (!employee) {
    return <p className="text-sm text-muted-foreground">{t('NO_HR_RECORD_FOUND')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center gap-3">
        {!isCompletionRequired && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('BACK')}
            onClick={() => navigate(`${APP_PATHS.MY_PROFILE}?tab=${MY_PROFILE_TABS.OVERVIEW}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <h1 className="text-lg font-semibold">{t('EDIT_PROFILE')}</h1>
      </div>
      {/* The completion gate sends the person here from every other page; say why and what unlocks it. */}
      {isCompletionRequired && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-primary/40 bg-accent p-3 text-sm text-accent-foreground"
        >
          <Info className="mt-0.5 size-4 shrink-0" />
          <p>{t('COMPLETE_YOUR_PROFILE_TO_CONTINUE')}</p>
        </div>
      )}
      <EmployeeEditTabs
        key={employee.ItemId}
        mode="self"
        employee={employee}
        skillLookups={{ subCategories: taxonomy?.subCategories ?? [], skills }}
      />
    </div>
  );
};
